import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import type { Database } from "../lib/database.types";
import type { Prediction } from "../types";

type PredictionRow = Database["public"]["Tables"]["predictions"]["Row"];

interface PredictionWithMatch extends PredictionRow {
  matches: Database["public"]["Tables"]["matches"]["Row"];
  profiles: Database["public"]["Tables"]["profiles"]["Row"];
}

interface PredictionWithUser extends Prediction {
  userName?: string;
  userId?: string;
  createdAt?: string;
  userTotalPoints?: number;
}

export function usePredictions(matchId?: string, userId?: string, leagueId?: string) {
  const [predictions, setPredictions] = useState<PredictionWithUser[]>([]);
  const [userPrediction, setUserPrediction] = useState<PredictionRow | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchPredictions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // If squad mode, filter predictions by squad members
      if (leagueId) {
        // Get squad member IDs first
        const { data: members, error: membersError } = await supabase
          .from("squad_members")
          .select("user_id")
          .eq("squad_id", leagueId);

        if (membersError) throw membersError;

        const memberIds = members?.map(m => m.user_id) || [];

        if (memberIds.length === 0) {
          setPredictions([]);
          setLoading(false);
          return;
        }

        // Fetch predictions only from squad members
        let query = supabase
          .from("predictions")
          .select(
            `
            *,
            matches (*),
            profiles (id, name, total_points)
          `
          )
          .in("user_id", memberIds)
          .order("created_at", { ascending: false });

        if (matchId) {
          query = query.eq("match_id", matchId);
        }

        const { data, error: fetchError } = await query;

        if (fetchError) throw fetchError;

        const mappedPredictions = ((data as PredictionWithMatch[]) || []).map(
          (pred) => {
            const match = pred.matches;
            const profile = pred.profiles;
            return {
              homeTeam: match?.home_country_code || "",
              awayTeam: match?.away_country_code || "",
              homeFlag: match?.home_flag || "",
              awayFlag: match?.away_flag || "",
              homeScore: pred.home_score,
              awayScore: pred.away_score,
              mood: pred.mood,
              userName: profile?.name,
              userId: pred.user_id,
              createdAt: pred.created_at,
              userTotalPoints: profile?.total_points || 0,
            };
          }
        );

        setPredictions(mappedPredictions);
      } else {
        // Global mode - fetch all predictions
        let query = supabase
          .from("predictions")
          .select(
            `
            *,
            matches (*),
            profiles (id, name, total_points)
          `
          )
          .order("created_at", { ascending: false });

        if (matchId) {
          query = query.eq("match_id", matchId);
        }

        const { data, error: fetchError } = await query;

        if (fetchError) throw fetchError;

        const mappedPredictions = ((data as PredictionWithMatch[]) || []).map(
          (pred) => {
            const match = pred.matches;
            const profile = pred.profiles;
            // Store country codes instead of translated names
            // Components will translate dynamically using useCountryTranslations
            return {
              homeTeam: match?.home_country_code || "",
              awayTeam: match?.away_country_code || "",
              homeFlag: match?.home_flag || "",
              awayFlag: match?.away_flag || "",
              homeScore: pred.home_score,
              awayScore: pred.away_score,
              mood: pred.mood,
              userName: profile?.name,
              userId: pred.user_id,
              createdAt: pred.created_at,
              userTotalPoints: profile?.total_points || 0,
            };
          }
        );

        setPredictions(mappedPredictions);
      }

      // If userId is provided, fetch the user's specific prediction separately
      if (userId && matchId) {
        const { data: userPredData, error: userPredError } = await supabase
          .from("predictions")
          .select("*")
          .eq("match_id", matchId)
          .eq("user_id", userId)
          .maybeSingle();

        if (userPredError && userPredError.code !== "PGRST116") {
          console.error('Failed to fetch user prediction:', userPredError);
        }

        setUserPrediction(userPredData || null);
      } else {
        setUserPrediction(null);
      }
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error("Failed to fetch predictions");
      setError(error);
      console.error('Failed to fetch predictions:', err);
    } finally {
      setLoading(false);
    }
  }, [matchId, userId, leagueId]);

  useEffect(() => {
    fetchPredictions();

    // Subscribe to real-time updates
    const channel = supabase
      .channel("predictions-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "predictions",
        },
        () => {
          fetchPredictions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchPredictions]);

  const createPrediction = useCallback(
    async (matchId: string, prediction: Prediction, userId: string) => {
      try {
        // Check if prediction already exists (one prediction per user per match, global)
        const { data: existing, error: checkError } = await supabase
          .from("predictions")
          .select("id")
          .eq("user_id", userId)
          .eq("match_id", matchId)
          .maybeSingle();

        // Ignore "not found" errors, only throw other errors
        if (checkError && checkError.code !== "PGRST116") {
          throw checkError;
        }

        const predictionData: Database["public"]["Tables"]["predictions"]["Insert"] = {
          user_id: userId,
          match_id: matchId,
          home_score: prediction.homeScore,
          away_score: prediction.awayScore,
          mood: prediction.mood,
        };

        let result;
        if (existing) {
          // Update existing prediction
          result = await supabase
            .from("predictions")
            .update(predictionData)
            .eq("id", existing.id)
            .select()
            .single();
        } else {
          // Insert new prediction
          result = await supabase
            .from("predictions")
            .insert(predictionData)
            .select()
            .single();

          // Update user's prediction count only for new predictions
          if (result.data && !result.error) {
            const { error: rpcError } = await supabase.rpc("increment_predictions", { user_id: userId });
            if (rpcError) {
              // Silently fail - prediction was created successfully
              // The count will be updated on next profile refresh
            }
            
            // Update achievement tracking
            await updateAchievementProgress(userId, prediction);
          }
        }

        if (result.error) throw result.error;

        return { data: result.data, error: null };
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create prediction');
        return { data: null, error };
      }
    },
    []
  );

  // Helper function to update achievement progress
  const updateAchievementProgress = async (
    userId: string,
    prediction: Prediction
  ) => {
    try {
      // Get current profile data
      const { data: profile } = await supabase
        .from("profiles")
        .select("bold_predictions, last_prediction_date, prediction_streak")
        .eq("id", userId)
        .single();

      if (!profile) return;

      const updates: Partial<Database["public"]["Tables"]["profiles"]["Update"]> = {};

      // Track bold predictions
      if (prediction.mood === "bold") {
        updates.bold_predictions = (profile.bold_predictions ?? 0) + 1;
      }

      // Track prediction streak
      const today = new Date().toISOString().split("T")[0];
      const lastDate = profile.last_prediction_date;
      const yesterday = new Date(Date.now() - 86400000)
        .toISOString()
        .split("T")[0];

      if (lastDate === yesterday) {
        // Continue streak
        updates.prediction_streak = (profile.prediction_streak ?? 0) + 1;
      } else if (lastDate !== today) {
        // Start new streak
        updates.prediction_streak = 1;
      }
      // If lastDate === today, don't update streak (already predicted today)

      updates.last_prediction_date = today;

      // Apply updates
      if (Object.keys(updates).length > 0) {
        await supabase.from("profiles").update(updates).eq("id", userId);
      }
    } catch (error) {
      console.error('Failed to update achievement progress:', error);
    }
  };

  return {
    predictions,
    userPrediction,
    loading,
    error,
    createPrediction,
    refetch: fetchPredictions,
  };
}
